import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { PhotoViewerProps } from './photo-viewer/types';
import { usePhotoNavigation } from './photo-viewer/usePhotoNavigation';
import { usePhotoZoomPan } from './photo-viewer/usePhotoZoomPan';
import { usePhotoAnnotation } from './photo-viewer/usePhotoAnnotation';
import { AnnotationToolPanel } from './photo-viewer/AnnotationToolPanel';
import { ZoomInIcon, ZoomOutIcon, ResetIcon, DownloadIcon, TrashIcon, PenIcon } from './photo-viewer/icons';
import { Button } from '@safetyhub/ui';

export type { Photo, AnnotationConfig, PhotoViewerProps } from './photo-viewer/types';

export function PhotoViewer({
  photos,
  currentIndex,
  isOpen,
  onClose,
  onIndexChange,
  onDelete,
  onDownload,
  annotation,
}: PhotoViewerProps) {
  const hasAnnotation = !!annotation;

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // 캔버스 크기 (어노테이션용)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // safeIndex 계산 (nav 훅 전에 photoId가 필요하므로)
  const [localIndex, setLocalIndex] = useState(currentIndex);
  useEffect(() => { setLocalIndex(currentIndex); }, [currentIndex]);
  const safeIndex = photos.length > 0 ? Math.min(localIndex, photos.length - 1) : 0;
  const currentPhotoId = photos[safeIndex]?.id;

  // 줌/패닝
  const zoomContainerRef = hasAnnotation ? imageContainerRef : containerRef;
  const zoomPan = usePhotoZoomPan({
    containerRef: zoomContainerRef,
    isOpen,
    centerBasedZoom: hasAnnotation,
  });

  // 어노테이션 (항상 호출, config가 없으면 내부에서 비활성)
  const anno = usePhotoAnnotation({
    config: annotation,
    currentPhotoId,
    isOpen,
    canvasRef,
    canvasSize,
    zoom: zoomPan.zoom,
  });

  // 네비게이션 (anno.beforeNavigate/beforeClose를 ref로 참조하므로 안전)
  const nav = usePhotoNavigation({
    photosCount: photos.length,
    currentIndex,
    isOpen,
    onClose,
    onIndexChange,
    beforeNavigate: hasAnnotation ? anno.beforeNavigate : undefined,
    isZoomed: zoomPan.isZoomed,
    resetZoom: zoomPan.resetZoom,
    beforeClose: hasAnnotation ? anno.beforeClose : undefined,
  });

  // nav.index를 localIndex에 동기화 (nav가 safeIndex를 보정할 수 있으므로)
  useEffect(() => { setLocalIndex(nav.index); }, [nav.index]);

  // 사진 변경 시 줌/캔버스 리셋
  useEffect(() => {
    zoomPan.resetZoom();
    setCanvasSize({ width: 0, height: 0 });
  }, [nav.index]);

  const currentPhoto = photos.length > 0 ? photos[nav.index] : null;

  if (!isOpen || photos.length === 0 || !currentPhoto) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9998] bg-black flex flex-col">
      {/* 헤더 */}
      <Header
        nav={nav}
        zoomPan={zoomPan}
        anno={anno}
        photosCount={photos.length}
        currentPhoto={currentPhoto}
        onDelete={onDelete}
        onDownload={onDownload}
        hasAnnotation={hasAnnotation}
      />

      {/* 메인 영역 */}
      <div className={`flex-1 flex overflow-hidden ${
        hasAnnotation && !anno.readonly && anno.panelLayout === 'side' ? 'flex-col sm:flex-row' : 'flex-col'
      }`}>
        {/* 사진 + 캔버스 영역 */}
        <PhotoArea
          containerRef={containerRef}
          imageContainerRef={imageContainerRef}
          imageRef={imageRef}
          canvasRef={canvasRef}
          currentPhoto={currentPhoto}
          nav={nav}
          zoomPan={zoomPan}
          anno={anno}
          hasAnnotation={hasAnnotation}
          canvasSize={canvasSize}
          setCanvasSize={setCanvasSize}
        />

        {/* 어노테이션 도구 패널 */}
        {hasAnnotation && !anno.readonly && (
          <>
            {/* 데스크탑 사이드 패널 */}
            <div className={`${anno.panelLayout === 'side' ? 'hidden sm:flex' : 'hidden'}`}>
              <AnnotationToolPanel
                layout="side"
                showAnnotation={anno.showAnnotation}
                setShowAnnotation={anno.setShowAnnotation}
                activeTool={anno.activeTool}
                setActiveTool={anno.setActiveTool}
                isEraserMode={anno.isEraserMode}
                currentColor={anno.currentColor}
                setCurrentColor={anno.setCurrentColor}
                currentWidth={anno.currentWidth}
                setCurrentWidth={anno.setCurrentWidth}
                customColor={anno.customColor}
                setCustomColor={anno.setCustomColor}
                strokeCount={anno.strokes.length}
                onClearAll={anno.handleClearAll}
              />
            </div>

            {/* 하단 패널 (모바일 항상 + 데스크탑에서 bottom 선택 시) */}
            <div className={`${anno.panelLayout === 'bottom' ? 'flex flex-col' : 'flex flex-col sm:hidden'}`}>
              <AnnotationToolPanel
                layout="bottom"
                showAnnotation={anno.showAnnotation}
                setShowAnnotation={anno.setShowAnnotation}
                activeTool={anno.activeTool}
                setActiveTool={anno.setActiveTool}
                isEraserMode={anno.isEraserMode}
                currentColor={anno.currentColor}
                setCurrentColor={anno.setCurrentColor}
                currentWidth={anno.currentWidth}
                setCurrentWidth={anno.setCurrentWidth}
                customColor={anno.customColor}
                setCustomColor={anno.setCustomColor}
                strokeCount={anno.strokes.length}
                onClearAll={anno.handleClearAll}
              />
            </div>
          </>
        )}
      </div>

      {/* 캡션 (어노테이션 없는 모드에서만) */}
      {!hasAnnotation && currentPhoto.caption && (
        <div className="px-4 py-3 bg-black/50 text-center">
          <p className="text-white text-sm">{currentPhoto.caption}</p>
        </div>
      )}

      {/* 썸네일 스트립 (2장 이상일 때) */}
      {photos.length > 1 && (
        <div className="px-4 py-3 bg-black/70 overflow-x-auto">
          <div className="flex gap-2 justify-center">
            {photos.map((photo, i) => (
              <button
                key={photo.id}
                type="button"
                onClick={() => nav.changePhoto(i)}
                className={`
                  w-16 h-16 flex-shrink-0 rounded-card-sm overflow-hidden border-2 transition-all
                  ${i === nav.index
                    ? 'border-white opacity-100'
                    : 'border-transparent opacity-60 hover:opacity-80'
                  }
                `}
              >
                <img
                  src={photo.thumbnailUrl || photo.url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

// ============================
// Header sub-component
// ============================
function Header({
  nav,
  zoomPan,
  anno,
  photosCount,
  currentPhoto,
  onDelete,
  onDownload,
  hasAnnotation,
}: {
  nav: ReturnType<typeof usePhotoNavigation>;
  zoomPan: ReturnType<typeof usePhotoZoomPan>;
  anno: ReturnType<typeof usePhotoAnnotation>;
  photosCount: number;
  currentPhoto: { id: string };
  onDelete?: (photoId: string) => void;
  onDownload?: (photoId: string) => void;
  hasAnnotation: boolean;
}) {
  return (
    <div className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3 bg-black/50 z-20">
      {/* 왼쪽: 닫기 + 사진 순번 */}
      <div className="flex items-center gap-2 sm:gap-4">
        <button
          type="button"
          onClick={nav.handleClose}
          className="text-white hover:text-ink-300 p-1 sm:p-2"
        >
          <span className="text-xl sm:text-2xl">&times;</span>
        </button>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {photosCount > 1 && (
            <span className="text-white text-xs sm:text-sm whitespace-nowrap">
              {nav.index + 1}/{photosCount}
            </span>
          )}
          {hasAnnotation && anno.hasUnsavedChanges && (
            <span className="text-yellow-400 text-[10px] sm:text-xs px-1 py-0.5 bg-yellow-400/20 rounded">수정됨</span>
          )}
        </div>

        {/* 줌 상태 힌트 (단순 뷰어) */}
        {!hasAnnotation && zoomPan.isZoomed && (
          <span className="text-white/60 text-xs hidden sm:inline">
            스크롤로 확대/축소 · 드래그로 이동 · {Math.round(zoomPan.zoom * 100)}%
          </span>
        )}
      </div>

      {/* 오른쪽: 줌 컨트롤 + 액션 버튼 */}
      <div className="flex items-center gap-1.5 sm:gap-3">
        {/* 줌 컨트롤 [−] [100%] [+] */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={zoomPan.handleZoomOut}
            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-white bg-white/10 hover:bg-white/20 rounded"
            title="축소"
          >
            <ZoomOutIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
          <span className="text-white text-xs sm:text-sm min-w-[36px] sm:min-w-[50px] text-center">
            {Math.round(zoomPan.zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={zoomPan.handleZoomIn}
            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-white bg-white/10 hover:bg-white/20 rounded"
            title="확대"
          >
            <ZoomInIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>

          {/* 원래 뷰 버튼 */}
          {(zoomPan.zoom !== 1 || zoomPan.pan.x !== 0 || zoomPan.pan.y !== 0) && (
            <button
              type="button"
              onClick={zoomPan.resetZoom}
              className="w-7 h-7 sm:w-auto sm:h-auto sm:px-2 sm:py-1 text-white text-xs bg-white/20 hover:bg-white/30 rounded flex items-center justify-center gap-1"
              title="원래 뷰로 (확대/위치 초기화)"
            >
              <ResetIcon className="w-3 h-3" />
              <span className="hidden sm:inline">원래 뷰</span>
            </button>
          )}
        </div>

        {/* readonly 모드: 메모 보기 토글 */}
        {hasAnnotation && anno.readonly && !anno.skipAnnotation && (
          <button
            type="button"
            onClick={() => anno.setShowAnnotation(!anno.showAnnotation)}
            className={`px-2 py-1.5 rounded text-xs transition-colors flex items-center gap-1 ${
              anno.showAnnotation
                ? 'bg-brand-500 text-white'
                : 'bg-white/10 text-ink-300 hover:bg-white/20'
            }`}
            title="그리기 메모 표시/숨기기"
          >
            <PenIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">메모</span>
          </button>
        )}

        {/* 패널 배치 토글 (편집 모드) */}
        {hasAnnotation && !anno.readonly && (
          <button
            type="button"
            onClick={() => anno.setPanelLayout(l => l === 'side' ? 'bottom' : 'side')}
            className="hidden sm:flex w-7 h-7 sm:w-8 sm:h-8 items-center justify-center text-white bg-white/10 hover:bg-white/20 rounded"
            title={anno.panelLayout === 'side' ? '도구 패널 하단 배치' : '도구 패널 측면 배치'}
          >
            {anno.panelLayout === 'side' ? (
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <rect x="2" y="2" width="16" height="16" rx="2" />
                <line x1="2" y1="13" x2="18" y2="13" />
              </svg>
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <rect x="2" y="2" width="16" height="16" rx="2" />
                <line x1="13" y1="2" x2="13" y2="18" />
              </svg>
            )}
          </button>
        )}

        {/* 저장 버튼 */}
        {hasAnnotation && !anno.readonly && anno.hasUnsavedChanges && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="whitespace-nowrap"
            onClick={anno.handleSave}
            disabled={anno.isSaving}
          >
            {anno.isSaving ? '...' : '저장'}
          </Button>
        )}

        {/* 다운로드 */}
        {onDownload && (
          <button
            type="button"
            onClick={() => onDownload(currentPhoto.id)}
            className="text-white hover:text-ink-300 p-2"
            title="다운로드"
          >
            <DownloadIcon />
          </button>
        )}

        {/* 삭제 */}
        {onDelete && (
          <button
            type="button"
            onClick={() => {
              if (confirm('이 사진을 삭제하시겠습니까?')) {
                onDelete(currentPhoto.id);
              }
            }}
            className="text-red-400 hover:text-red-300 p-2"
            title="삭제"
          >
            <TrashIcon />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================
// PhotoArea sub-component
// ============================
function PhotoArea({
  containerRef,
  imageContainerRef,
  imageRef,
  canvasRef,
  currentPhoto,
  nav,
  zoomPan,
  anno,
  hasAnnotation,
  canvasSize,
  setCanvasSize,
}: {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  imageContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  imageRef: React.MutableRefObject<HTMLImageElement | null>;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  currentPhoto: { id: string; url: string; caption?: string };
  nav: ReturnType<typeof usePhotoNavigation>;
  zoomPan: ReturnType<typeof usePhotoZoomPan>;
  anno: ReturnType<typeof usePhotoAnnotation>;
  hasAnnotation: boolean;
  canvasSize: { width: number; height: number };
  setCanvasSize: (s: { width: number; height: number }) => void;
}) {
  // 이미지 로드 시 캔버스 크기 설정 (어노테이션 모드)
  const handleImageLoad = useCallback(() => {
    if (!hasAnnotation || !imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const container = containerRef.current;

    const containerRect = container.getBoundingClientRect();
    const maxWidth = containerRect.width - 100;
    const maxHeight = containerRect.height - 32;

    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    const naturalRatio = naturalWidth / naturalHeight;

    let displayWidth, displayHeight;
    if (naturalRatio > maxWidth / maxHeight) {
      displayWidth = Math.min(maxWidth, naturalWidth);
      displayHeight = displayWidth / naturalRatio;
    } else {
      displayHeight = Math.min(maxHeight, naturalHeight);
      displayWidth = displayHeight * naturalRatio;
    }

    setCanvasSize({ width: displayWidth, height: displayHeight });
  }, [hasAnnotation]);

  // 단순 모드: 이미지 클릭
  const handleImageClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (zoomPan.hasDraggedRef.current) {
      zoomPan.hasDraggedRef.current = false;
      return;
    }
    zoomPan.handleZoomToggle();
  }, [zoomPan.handleZoomToggle]);

  if (hasAnnotation) {
    // ===== 어노테이션 모드 =====
    return (
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center relative overflow-hidden"
      >
        {/* 이전 버튼 */}
        {nav.hasPrevious && (
          <button
            type="button"
            onClick={nav.goToPrevious}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors z-10"
          >
            <span className="text-xl sm:text-2xl">&#8249;</span>
          </button>
        )}

        {/* 이미지 + 캔버스 컨테이너 */}
        <div
          ref={imageContainerRef}
          className="relative"
          style={{
            width: canvasSize.width || 'auto',
            height: canvasSize.height || 'auto',
            ...zoomPan.imageTransformStyle,
          }}
        >
          <img
            ref={imageRef}
            src={currentPhoto.url}
            alt={currentPhoto.caption || '점검 사진'}
            style={{ width: canvasSize.width || 'auto', height: canvasSize.height || 'auto' }}
            onLoad={handleImageLoad}
            draggable={false}
          />

          {/* 그리기 캔버스 */}
          {canvasSize.width > 0 && (
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              className={`absolute top-0 left-0 ${
                zoomPan.isPanning ? 'cursor-grabbing'
                : (anno.readonly || anno.isPanTool || !anno.showAnnotation) ? 'cursor-grab'
                : anno.isEraserMode ? 'cursor-pointer'
                : 'cursor-crosshair'
              }`}
              style={{ touchAction: 'none' }}
              onPointerDown={(e) => anno.handleDrawPointerDown(e, zoomPan.handlePanStart)}
              onPointerMove={(e) => anno.handleDrawPointerMove(e, zoomPan.isPanning, zoomPan.handlePanMove)}
              onPointerUp={(e) => anno.handleDrawPointerUp(e, zoomPan.isPanning, zoomPan.handlePanEnd)}
              onPointerLeave={(e) => anno.handleDrawPointerUp(e, zoomPan.isPanning, zoomPan.handlePanEnd)}
            />
          )}

          {/* 로딩 표시 */}
          {anno.isLoadingAnnotation && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="text-white text-sm">불러오는 중...</div>
            </div>
          )}
        </div>

        {/* 다음 버튼 */}
        {nav.hasNext && (
          <button
            type="button"
            onClick={nav.goToNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors z-10"
          >
            <span className="text-xl sm:text-2xl">&#8250;</span>
          </button>
        )}
      </div>
    );
  }

  // ===== 단순 뷰어 모드 =====
  return (
    <div
      ref={containerRef}
      className={`flex-1 flex items-center justify-center overflow-hidden relative ${
        zoomPan.isZoomed ? 'cursor-grab' : ''
      } ${zoomPan.isPanning ? 'cursor-grabbing' : ''}`}
      style={{ touchAction: 'none' }}
      onClick={zoomPan.isZoomed ? undefined : nav.handleClose}
      onPointerDown={(e) => {
        if (!zoomPan.isZoomed) return;
        zoomPan.handlePanStart(e);
      }}
      onPointerMove={zoomPan.handlePanMove}
      onPointerUp={zoomPan.handlePanEnd}
    >
      {/* 이전 버튼 (확대 안 됐을 때만) */}
      {nav.hasPrevious && !zoomPan.isZoomed && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            nav.goToPrevious();
          }}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors z-10"
        >
          <span className="text-xl sm:text-2xl">&#8249;</span>
        </button>
      )}

      {/* 사진 */}
      <img
        src={currentPhoto.url}
        alt={currentPhoto.caption || '점검 사진'}
        draggable={false}
        className={`
          max-h-full max-w-full object-contain select-none
          ${zoomPan.isZoomed ? '' : 'transition-transform duration-200'}
          ${zoomPan.isZoomed ? 'cursor-grab' : 'cursor-zoom-in'}
          ${zoomPan.isPanning ? 'cursor-grabbing' : ''}
        `}
        style={zoomPan.isZoomed ? zoomPan.imageTransformStyle : undefined}
        onClick={handleImageClick}
      />

      {/* 다음 버튼 (확대 안 됐을 때만) */}
      {nav.hasNext && !zoomPan.isZoomed && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            nav.goToNext();
          }}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors z-10"
        >
          <span className="text-xl sm:text-2xl">&#8250;</span>
        </button>
      )}
    </div>
  );
}

/**
 * 단일 사진 뷰어 (간단 버전)
 */
export function SinglePhotoViewer({
  url,
  caption,
  isOpen,
  onClose,
}: {
  url: string;
  caption?: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9998] bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-ink-300 p-2 z-10"
      >
        <span className="text-3xl">&times;</span>
      </button>

      <img
        src={url}
        alt={caption || '사진'}
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {caption && (
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <p className="text-white text-sm bg-black/50 inline-block px-4 py-2 rounded-card">
            {caption}
          </p>
        </div>
      )}
    </div>,
    document.body,
  );
}

export default PhotoViewer;
